<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import { formatUserDetail, summarizeForUser } from '@/errors';
import { useErrorStore } from '@/stores/error';
import { ButtonType, CustomButton } from '@/components/common/';

/**
 * Report a problem to Studypages.
 *
 * Deliberately spare. A coordinator is not a developer, so the default view is one sentence, the
 * reference, an optional note and two buttons. "Show more" reveals a short labelled summary — never
 * the raw payload: the stack, the trail and the reproduction ids are attached silently.
 *
 * `formatUserDetail` is a separate formatter from `formatDiagnosticReport` on purpose, so that adding
 * a field to the report can never widen what this dialog shows.
 */
const errorStore = useErrorStore();
const { t: $t } = useI18n();

const note = ref('');
const showDetail = ref(false);

const target = computed(() => errorStore.reportTarget);
const summary = computed(() => target.value ? summarizeForUser(target.value.error) : undefined);
const detailRows = computed(() => target.value ? formatUserDetail(target.value.report) : []);
const isSubmitting = computed(() => errorStore.submitState === 'sending');
const isDone = computed(() => errorStore.submitState === 'sent' || errorStore.submitState === 'emailed');
/**
 * A failed send keeps the form, it does not replace it.
 *
 * The store cannot open the mail client for them — `window.open` needs the click's transient user
 * activation and the API round-trip has spent it — so the recovery is the user pressing "Email
 * support instead", which needs the buttons and the note still to be there.
 */
const hasFailed = computed(() => errorStore.submitState === 'failed');

// A fresh dialog each time — a stale note from a previous failure would be worse than none.
watch(target, value => {
  if (value) {
    note.value = '';
    showDetail.value = false;
  }
});

const close = () => errorStore.closeReport();
</script>

<template>
  <Dialog :visible="!!target"
    modal
    :header="$t('error_report_title')"
    :style="{ width: '480px' }"
    @update:visible="close">
    <div class="report-dialog" v-if="target">
      <template v-if="isDone">
        <p class="report-dialog-sent">
          {{ errorStore.submitState === 'sent' ? $t('error_report_sent') : $t('error_report_emailed') }}
        </p>
        <p class="report-dialog-reference">
          {{ $t('error_reference') }} <code>{{ summary?.reference }}</code>
        </p>
        <p class="report-dialog-hint" v-if="errorStore.reloadPending">
          {{ $t('error_report_reloading') }}
        </p>
        <div class="report-dialog-actions">
          <CustomButton :text="$t('cancel')"
            :type="ButtonType.OUTLINED"
            @onClick="close"/>
          <CustomButton :text="$t('error_reload_page')"
            v-if="!errorStore.reloadPending"
            :margin-left="12"
            :type="ButtonType.POSITIVE"
            @onClick="errorStore.reloadApp()"/>
        </div>
      </template>

      <template v-else>
        <p class="report-dialog-summary">{{ summary?.message }}</p>
        <p class="report-dialog-reference">
          {{ $t('error_reference') }} <code>{{ summary?.reference }}</code>
        </p>

        <label class="report-dialog-label" for="error-report-note">
          {{ $t('error_report_what_happened') }}
        </label>
        <Textarea id="error-report-note"
          v-model="note"
          rows="3"
          autoResize
          class="report-dialog-note"/>
        <p class="report-dialog-hint">{{ $t('error_report_phi_warning') }}</p>

        <button type="button" class="report-dialog-toggle" @click="showDetail = !showDetail">
          {{ showDetail ? $t('error_report_show_less') : $t('error_report_show_more') }}
        </button>
        <dl class="report-dialog-detail" v-if="showDetail">
          <template v-for="row in detailRows" :key="row.label">
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          </template>
        </dl>

        <p class="report-dialog-hint">{{ $t('error_report_auto_details') }}</p>
        <p class="report-dialog-failed" v-if="hasFailed">{{ $t('error_report_failed') }}</p>

        <!-- Reload is a different kind of action from the two reporting ones, so it sits apart
             from them rather than becoming a third button in the same row. -->
        <div class="report-dialog-actions report-dialog-actions--split">
          <CustomButton :text="$t('error_reload_page')"
            :type="ButtonType.WITHOUT_OUTLINE"
            @onClick="errorStore.reloadApp()"/>
          <div class="report-dialog-actions-report">
            <CustomButton :text="$t('error_report_email_instead')"
              :type="ButtonType.OUTLINED"
              @onClick="errorStore.emailSupport(note)"/>
            <CustomButton :text="$t('error_report_send')"
              :margin-left="12"
              :loading="isSubmitting"
              :type="ButtonType.POSITIVE"
              @onClick="errorStore.submitReport(note)"/>
          </div>
        </div>
      </template>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
.report-dialog {
  display: flex;
  flex-direction: column;

  &-summary {
    margin: 0;
    font-size: 14px;
    color: #272a2e;
  }

  &-sent {
    margin: 0;
    font-size: 14px;
    color: #272a2e;
  }

  &-reference {
    margin: 8px 0 0;
    font-size: 12px;
    color: #6b7075;

    code {
      font-size: 12px;
      letter-spacing: 0.04em;
    }
  }

  &-label {
    margin: 20px 0 6px;
    font-size: 13px;
    font-weight: 500;
    color: #272a2e;
  }

  &-note {
    width: 100%;
    font-size: 14px;
  }

  &-hint {
    margin: 8px 0 0;
    font-size: 12px;
    color: #6b7075;
  }

  &-failed {
    margin: 12px 0 0;
    font-size: 13px;
    color: #b3261e;
  }

  &-toggle {
    align-self: flex-start;
    margin-top: 16px;
    padding: 0;
    background: none;
    border: none;
    font-size: 13px;
    color: #6b7075;
    text-decoration: underline;
    text-underline-offset: 4px;
    cursor: pointer;
  }

  &-detail {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 4px 12px;
    margin: 12px 0 0;
    font-size: 12px;

    dt {
      color: #6b7075;
      white-space: nowrap;
    }

    dd {
      margin: 0;
      color: #272a2e;
      overflow-wrap: anywhere;
    }
  }

  &-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 28px;

    // Three buttons do not fit one line on a narrow viewport, and the reporting pair has to stay
    // together when they wrap — so the row wraps, not the group inside it.
    &--split {
      flex-wrap: wrap;
      justify-content: space-between;
      gap: 12px;
    }

    &-report {
      display: flex;
      margin-left: auto;
    }
  }
}
</style>
